(() => {
  'use strict';
  const TAG = 'toba-charm-quiz';
  if (customElements.get(TAG)) return;
  const shuffle = values => {
    const result = [...values];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  };
  class TobaQuiz extends HTMLElement {
    connectedCallback() {
      if (this.started) {
        document.addEventListener('visibilitychange', this.onVisibility);
        return;
      }
      this.timer = setTimeout(() => this.init(), 0);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      if (!this.started) return;
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.soundEnabled = false;
      this.stopSounds();
      const context = this.audioContext;
      this.audioContext = null;
      if (context && context.state !== 'closed') context.close().catch(() => {});
      this.updateSoundButton();
      this.clearShareImage();
    }
    init() {
      if (this.started || !this.isConnected) return;
      const settings = this.querySelector('script[type="application/json"]');
      const template = this.querySelector('template');
      if (!settings || !template) return;
      this.config = JSON.parse(settings.textContent);
      this.data = this.config.animals;
      this.text = this.config.text;
      this.started = true;
      this.root = this.attachShadow({ mode: 'open' });
      this.root.append(template.content.cloneNode(true));
      this.app = this.root.querySelector('.toba-quiz-app');
      this.main = this.root.querySelector('main');
      this.soundEnabled = false;
      this.activeSounds = new Set();
      this.audioGeneration = 0;
      this.onVisibility = () => { if (document.hidden) this.stopSounds(); };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.soundButton = this.button('', 'sound');
      this.soundButton.className = 'toba-quiz-sound-toggle';
      this.soundStatus = this.el('span', 'sr');
      this.soundStatus.setAttribute('role', 'status');
      const top = this.root.querySelector('.toba-quiz-top');
      top.insertBefore(this.soundButton, top.querySelector('.toba-quiz-badge'));
      top.append(this.soundStatus);
      this.updateSoundButton();
      this.saved = new Set();
      this.storageOK = true;
      try {
        const raw = localStorage.getItem(this.config.storageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.version === 1 && Array.isArray(parsed.ids)) {
            this.saved = new Set(parsed.ids.filter(id => this.data.some(a => a.id === id)));
          }
        }
      } catch (error) {
        if (!(error instanceof SyntaxError)) this.storageOK = false;
      }
      this.root.addEventListener('click', event => {
        const button = event.target.closest('[data-action]');
        if (!button || button.disabled) return;
        const action = button.dataset.action;
        if (action === 'animal' || action === 'name') this.start(action);
        else if (action === 'answer') this.answer(Number(button.dataset.index));
        else if (action === 'next' && this.answered) {
          this.playSound('tap');
          this.index++;
          this.index < 5 ? this.question() : this.result();
        }
        else if (action === 'home') this.home();
        else if (action === 'collection') this.collection();
        else if (action === 'again') this.start(this.mode);
        else if (action === 'other') this.start(this.mode === 'animal' ? 'name' : 'animal');
        else if (action === 'share') this.share(button);
        else if (action === 'instagram') this.instagram(button);
        else if (action === 'copy-caption') this.copyCaption(button);
        else if (action === 'celebrate') this.celebrate(this.main.querySelector('.toba-quiz-complete'));
        else if (action === 'sound') this.toggleSound();
        else if (action === 'reset') this.resetPrompt();
        else if (action === 'cancel-reset') this.collection();
        else if (action === 'confirm-reset') this.reset();
        else if (action === 'select-share') {
          const area = this.main.querySelector('textarea');
          area.focus(); area.select();
        }
      });
      this.home(false);
    }
    updateSoundButton() {
      const available = !!(window.AudioContext || window.webkitAudioContext);
      this.soundButton.disabled = !available;
      this.soundButton.textContent = available ? (this.soundEnabled ? this.text.soundOn : this.text.soundOff) : this.text.soundUnavailableLabel;
      this.soundButton.setAttribute('aria-pressed', String(this.soundEnabled));
      this.soundButton.setAttribute('aria-label', this.soundEnabled ? this.text.soundDisable : this.text.soundEnable);
    }
    toggleSound() {
      this.soundError = false;
      if (this.soundEnabled) {
        this.soundEnabled = false;
        this.stopSounds();
      } else {
        try {
          const Context = window.AudioContext || window.webkitAudioContext;
          if (!Context) return;
          if (!this.audioContext || this.audioContext.state === 'closed') this.audioContext = new Context();
          this.soundEnabled = true;
          this.playSound('tap');
        } catch { this.soundEnabled = false; this.soundError = true; }
      }
      this.updateSoundButton();
      this.soundStatus.textContent = this.soundError ? this.text.soundUnavailable : this.soundEnabled ? this.text.soundOn : this.text.soundOff;
    }
    stopSounds() {
      this.audioGeneration++;
      this.activeSounds.forEach(({ oscillator, gain }) => {
        try { oscillator.stop(); } catch {}
        oscillator.disconnect(); gain.disconnect();
      });
      this.activeSounds.clear();
    }
    playSound(kind) {
      if (!this.soundEnabled || !this.audioContext || document.hidden) return;
      this.stopSounds();
      const generation = this.audioGeneration;
      const context = this.audioContext;
      const fail = () => {
        if (context !== this.audioContext || generation !== this.audioGeneration) return;
        this.soundError = true;
        this.soundEnabled = false;
        this.stopSounds(); this.updateSoundButton();
        this.soundStatus.textContent = this.text.soundUnavailable;
      };
      const schedule = () => {
        if (!this.soundEnabled || !this.isConnected || document.hidden || generation !== this.audioGeneration) return;
        try {
          const sound = this.config.sound;
          const notes = sound.sequences[kind] || sound.sequences.tap;
          notes.forEach((frequency, index) => {
            const oscillator = context.createOscillator();
            const gain = context.createGain();
            const at = context.currentTime + index * sound.step;
            oscillator.type = 'sine'; oscillator.frequency.setValueAtTime(frequency, at);
            gain.gain.setValueAtTime(0.0001, at);
            gain.gain.exponentialRampToValueAtTime(sound.volume, at + 0.012);
            gain.gain.exponentialRampToValueAtTime(0.0001, at + sound.duration);
            oscillator.connect(gain); gain.connect(context.destination);
            const active = { oscillator, gain };
            this.activeSounds.add(active);
            oscillator.onended = () => {
              oscillator.disconnect(); gain.disconnect(); this.activeSounds.delete(active);
            };
            oscillator.start(at); oscillator.stop(at + sound.duration + 0.02);
          });
        } catch { fail(); }
      };
      if (context.state === 'suspended') context.resume().then(schedule).catch(fail);
      else if (context.state === 'running') schedule();
      else fail();
    }
    burst(target, complete = false) {
      if (this.config.effects === false || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      const layer = this.el('div', 'burst'); layer.setAttribute('aria-hidden', 'true');
      const count = complete ? 16 : 10;
      for (let i = 0; i < count; i++) {
        const bubble = this.el('span', 'bubble');
        const angle = (Math.PI * 2 * i) / count;
        const distance = complete ? 95 : 65;
        bubble.style.setProperty('--toba-quiz-dx', `${Math.cos(angle) * distance}px`);
        bubble.style.setProperty('--toba-quiz-dy', `${Math.sin(angle) * distance - 22}px`);
        bubble.style.setProperty('--toba-quiz-delay', `${i * 12}ms`);
        bubble.style.background = ['#58bed4', '#f3cf6e', '#80c9ae', '#91b9e8'][i % 4];
        if (i === count - 1) bubble.addEventListener('animationend', () => layer.remove(), { once: true });
        layer.append(bubble);
      }
      target.append(layer);
    }
    el(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = 'toba-quiz-' + className;
      if (text !== undefined) node.textContent = text;
      return node;
    }
    button(text, action, secondary = false) {
      const b = this.el('button', 'btn' + (secondary ? ' toba-quiz-btn-secondary' : ''), text);
      b.type = 'button'; b.dataset.action = action;
      return b;
    }
    paragraph(text, cls = '') { return this.el('p', cls, text); }
    collectionButton() {
      const button = this.button(`${this.text.collection}　${this.saved.size}/10`, 'collection', true);
      if (this.saved.size !== 10) return button;
      button.classList.add('toba-quiz-collection-complete');
      button.setAttribute('aria-label', 'めじるしチャーム図鑑を見る 全10種類コンプリート 10/10');
      const crown = this.el('span', 'collection-crown', '♛'); crown.setAttribute('aria-hidden','true');
      const copy = this.el('span', 'collection-copy');
      copy.append(this.el('span', 'collection-achieved', 'ALL CHARMS COMPLETE'), this.el('strong', '', 'めじるしチャーム図鑑を見る'));
      button.replaceChildren(crown,copy,this.el('span', 'collection-count', '10/10'));
      return button;
    }
    heading(text) { const h = this.el('h2', '', text); h.tabIndex = -1; return h; }
    page(kind, title) {
      this.clearShareImage();
      this.screen = kind;
      this.main.replaceChildren();
      this.main.className = 'toba-quiz-main' + (kind === 'question' ? '' : ' toba-quiz-center');
      this.root.querySelector('.toba-quiz-badge').textContent = `図鑑 ${this.saved.size}/10`;
      if (title) this.main.append(this.heading(title));
    }
    focus() {
      const heading = this.main.querySelector('h1,h2');
      if (heading) { heading.focus({ preventScroll: true }); heading.scrollIntoView({ block: 'nearest' }); }
    }
    notice() {
      return this.paragraph(this.storageOK ? this.text.storage : this.text.storageUnavailable, 'note');
    }
    photo(animal, quiz = false, ready = null, lazy = true) {
      const box = this.el('div', 'photo');
      const img = this.el('img');
      img.alt = quiz ? 'クイズのチャーム写真' : `${animal.species}：${animal.name}のチャーム`;
      img.width = 360; img.height = 430;
      img.loading = lazy ? 'lazy' : 'eager'; img.decoding = 'async';
      const status = this.paragraph(this.text.loading, 'small');
      const error = this.el('div', 'photo-error'); error.hidden = true;
      error.append(this.paragraph(this.text.imageError));
      const retry = this.button(this.text.retry, 'retry', true);
      error.append(retry); img.style.visibility = 'hidden';
      const load = () => {
        img.hidden = false; img.style.visibility = 'hidden'; error.hidden = true; status.hidden = false;
        img.src = animal.image;
      };
      img.addEventListener('load', () => {
        if (!img.naturalWidth) return;
        img.hidden = false; img.style.visibility = 'visible'; status.hidden = true; error.hidden = true;
        if (ready) ready();
      });
      img.addEventListener('error', () => {
        img.hidden = true; status.hidden = true; error.hidden = false;
      });
      retry.addEventListener('click', () => { img.removeAttribute('src'); load(); });
      box.append(img, status, error); load();
      return box;
    }
    product(text = this.text.productShort) {
      const a = this.el('a', '', text); a.href = this.config.productURL;
      return a;
    }
    promotion() {
      const promo = this.el('div', 'promo');
      promo.append(this.paragraph(this.text.promo), this.paragraph(this.text.set, 'small'));
      const product = this.product(this.text.product); product.className = 'toba-quiz-btn';
      promo.append(product, this.paragraph(this.text.conditions, 'small'));
      return promo;
    }
    home(moveFocus = true) {
      this.page('home');
      this.main.append(this.paragraph('TOBA AQUARIUM · CHARM QUIZ', 'kicker'));
      const title = this.el('h1'); title.tabIndex = -1;
      title.append(document.createTextNode('この子はだあれ？'), document.createElement('br'), document.createTextNode('鳥羽水族館チャームクイズ'));
      this.main.append(title, this.paragraph(this.text.tagline, 'tagline'));
      const hero = this.el('div', 'hero');
      [this.data[0], this.data[8], this.data[3]].forEach(a => hero.append(this.photo(a, false, null, false)));
      this.main.append(hero, this.paragraph(this.text.duration, 'small'));
      const actions = this.el('div', 'actions');
      actions.append(this.button(this.text.animalMode, 'animal'), this.button(this.text.nameMode, 'name', true), this.collectionButton());
      this.main.append(actions, this.product(), this.notice());
      this.main.append(this.paragraph(this.text.soundHint, 'note'));
      if (moveFocus) this.focus();
    }
    preferred(values) {
      return [...shuffle(values.filter(a => !this.saved.has(a.id))), ...shuffle(values.filter(a => this.saved.has(a.id)))];
    }
    start(mode) {
      this.playSound('start');
      this.mode = mode; this.score = 0; this.index = 0;
      if (mode === 'animal') {
        const groups = [...new Set(this.data.map(a => a.species))].map(species => this.preferred(this.data.filter(a => a.species === species))[0]);
        this.questions = this.preferred(groups).slice(0, 5);
      } else this.questions = this.preferred(this.data).slice(0, 5);
      this.question();
    }
    choicesFor(animal) {
      let correct, pool;
      if (this.mode === 'animal') {
        correct = animal.species;
        pool = [...new Set(this.data.map(a => a.species))].filter(s => s !== correct);
      } else if (animal.pair) {
        correct = animal.name;
        const names = this.data.filter(a => !a.pair).map(a => a.name);
        pool = [];
        for (let i = 0; i < names.length; i++) for (let j = i + 1; j < names.length; j++) {
          if (animal.members.includes(names[i]) && animal.members.includes(names[j])) continue;
          pool.push(`${names[i]}・${names[j]}`);
        }
      } else if (animal.species === 'ラッコ') {
        correct = animal.name;
        const otherOtter = this.data.find(a => !a.pair && a.species === animal.species && a.id !== animal.id);
        const otherAnimal = shuffle(this.data.filter(a => !a.pair && a.species !== animal.species))[0];
        pool = [otherOtter.name, otherAnimal.name];
      } else {
        correct = animal.name;
        pool = this.data.filter(a => !a.pair && a.id !== animal.id).map(a => a.name);
      }
      this.correct = correct;
      return shuffle([correct, ...shuffle([...new Set(pool)]).slice(0, 2)]);
    }
    question() {
      this.answered = false; this.imageReady = false;
      this.current = this.questions[this.index];
      this.choices = this.choicesFor(this.current);
      this.page('question');
      const progress = this.el('div', 'progress');
      progress.append(this.paragraph(this.mode === 'animal' ? 'どうぶつ編' : 'おなまえ編', 'small'), this.paragraph(`第${this.index + 1}問／5問`, 'small'));
      const meter = this.el('div', 'meter'); const fill = this.el('span'); fill.style.width = `${(this.index + 1) * 20}%`; meter.append(fill);
      const title = this.mode === 'animal' ? this.text.speciesQuestion : this.current.pair ? this.text.pairQuestion : this.text.nameQuestion;
      this.main.append(progress, meter, this.heading(title));
      const grid = this.el('div', 'question-grid');
      const options = this.el('div', 'options');
      const animal = this.current;
      this.choices.forEach((choice, i) => {
        const b = this.button('', 'answer'); b.classList.add('toba-quiz-choice'); b.dataset.index = i; b.disabled = true;
        b.append(this.el('span', '', String(i + 1)), document.createTextNode(choice)); options.append(b);
      });
      grid.append(this.photo(animal, true, () => {
        if (this.current !== animal || this.screen !== 'question' || this.answered) return;
        this.imageReady = true;
        options.querySelectorAll('button').forEach(b => b.disabled = false);
      }, false), options);
      this.main.append(grid); this.focus();
      const next = this.questions[this.index + 1];
      if (next) { const preload = new Image(); preload.src = next.image; }
    }
    answer(index) {
      if (this.screen !== 'question' || this.answered || !this.imageReady || !Number.isInteger(index) || !this.choices[index]) return;
      this.answered = true;
      const correct = this.choices[index] === this.correct;
      if (correct) this.score++;
      this.main.querySelectorAll('[data-action="answer"]').forEach(b => {
        b.disabled = true;
        if (this.choices[Number(b.dataset.index)] === this.correct) {
          b.classList.add('toba-quiz-choice-correct');
          b.append(this.el('strong', 'choice-mark', '○ 正解'));
        } else if (Number(b.dataset.index) === index) {
          b.classList.add('toba-quiz-choice-wrong');
          b.append(this.el('strong', 'choice-mark', '×'));
        }
      });
      const isNew = !this.saved.has(this.current.id);
      this.saved.add(this.current.id);
      try {
        localStorage.setItem(this.config.storageKey, JSON.stringify({version: 1, ids: [...this.saved]}));
      } catch { this.storageOK = false; }
      this.root.querySelector('.toba-quiz-badge').textContent = `図鑑 ${this.saved.size}/10`;
      const feedback = this.el('div', 'feedback');
      feedback.classList.add(correct ? 'toba-quiz-feedback-correct' : 'toba-quiz-feedback-incorrect');
      feedback.setAttribute('role', 'region'); feedback.setAttribute('aria-label', '答え'); feedback.tabIndex = -1;
      feedback.append(this.el('h3', '', correct ? '○ せいかい！' : 'おしい！'), this.paragraph(`${this.current.species} ／ ${this.current.name}`));
      if (!correct) feedback.append(this.paragraph(`選んだ答え：${this.choices[index]}`, 'small'));
      if (isNew) feedback.append(this.paragraph(this.text.newEntry, 'new'));
      if (!this.storageOK) feedback.append(this.notice());
      const actions = this.el('div', 'actions'); actions.append(this.button(this.index === 4 ? this.text.showResult : this.text.next, 'next'));
      feedback.append(actions); this.main.append(feedback);
      this.playSound(isNew && this.saved.size === 10 ? 'complete' : correct ? (isNew ? 'newEntry' : 'correct') : 'incorrect');
      if (correct || isNew) this.burst(feedback, isNew && this.saved.size === 10);
      feedback.focus({preventScroll:true}); feedback.scrollIntoView({block:'nearest'});
    }
    result() {
      this.page('result', this.text.result);
      this.main.append(this.el('p', 'score', `5問中${this.score}問正解！`));
      const rank = this.score === 5 ? 3 : this.score === 4 ? 2 : this.score >= 2 ? 1 : 0;
      this.main.append(this.paragraph(this.text.ranks[rank], 'title'), this.paragraph(this.text.met, 'note'));
      const met = this.el('div', 'met');
      this.questions.forEach(a => {
        const f = this.el('figure'); f.append(this.photo(a), this.el('figcaption', '', a.name)); met.append(f);
      });
      this.main.append(met);
      const actions = this.el('div', 'actions');
      actions.append(this.button(this.text.again, 'again'), this.button(this.text.other, 'other', true), this.collectionButton());
      const share = this.button(this.text.share, 'share', true); share.disabled = !this.publicURL();
      actions.append(share); this.main.append(actions);
      this.main.append(this.socialButtons());
      if (!this.publicURL()) this.main.append(this.paragraph(this.text.shareUnavailable, 'small'));
      const sharing = this.el('div', 'sharing'); sharing.setAttribute('aria-live', 'polite'); this.main.append(sharing);
      this.main.append(this.promotion(), this.button(this.text.home, 'home', true)); this.focus();
      this.playSound(this.score === 5 ? 'complete' : 'result');
      this.burst(this.main.querySelector('.toba-quiz-title'), this.score === 5);
    }
    collection() {
      this.page('collection', this.text.collectionTitle);
      if (this.saved.size === 10) {
        const complete = this.el('div', 'complete');
        complete.append(this.paragraph('★ CHARM MASTER ★', 'master-badge'), this.paragraph(this.text.complete, 'complete-title'), this.paragraph('おめでとう！ 全10種類の仲間に出会えました。', 'complete-message'), this.paragraph(this.text.completeMessage, 'small'));
        complete.insertBefore(this.celebrationScene(), complete.querySelector('.toba-quiz-complete-message'));
        const replay = this.button('もう一度お祝いする', 'celebrate', true);
        replay.className = 'toba-quiz-link'; complete.append(replay);
        const product = this.product(this.text.product); product.className = 'toba-quiz-btn';
        complete.append(product, this.paragraph(this.text.set, 'small'));
        this.main.append(complete);
        if (!this.collectionCelebrated) {
          this.collectionCelebrated = true; this.celebrate(complete);
        }
        this.main.append(this.socialButtons());
        const sharing = this.el('div', 'sharing'); sharing.setAttribute('aria-live', 'polite'); this.main.append(sharing);
      } else this.main.append(this.paragraph(`${this.saved.size}/10 デザインに出会いました`, 'tagline'));
      const grid = this.el('div', 'collection');
      this.data.forEach(a => {
        const card = this.el('article', 'card');
        if (this.saved.has(a.id)) card.append(this.photo(a), this.paragraph(a.species), this.el('h3', '', a.name));
        else card.append(this.el('div', 'locked', '？'), this.paragraph(this.text.locked), this.el('h3', '', 'まだひみつ'));
        grid.append(card);
      });
      this.main.append(grid, this.paragraph(this.text.disclaimer, 'small'), this.notice());
      this.main.append(this.promotion());
      const actions = this.el('div', 'actions');
      actions.append(this.button(this.text.animalMode, 'animal'), this.button(this.text.nameMode, 'name', true), this.button(this.text.home, 'home', true));
      this.main.append(actions);
      const reset = this.button(this.text.reset, 'reset', true); reset.className = 'toba-quiz-link'; this.main.append(reset); this.focus();
    }
    resetPrompt() {
      if (this.main.querySelector('.toba-quiz-reset')) return;
      const box = this.el('div', 'reset'); box.setAttribute('role', 'group'); box.setAttribute('aria-label', '記録リセットの確認');
      box.append(this.paragraph(this.text.resetConfirm));
      const actions = this.el('div', 'actions'); const cancel = this.button('キャンセル', 'cancel-reset');
      actions.append(cancel, this.button('記録をリセットする', 'confirm-reset', true)); box.append(actions); this.main.append(box); cancel.focus();
    }
    reset() {
      this.collectionCelebrated = false;
      try { localStorage.removeItem(this.config.storageKey); this.saved.clear(); this.collection(); }
      catch {
        this.saved.clear(); this.storageOK = false; this.collection();
        this.main.append(this.paragraph(this.text.resetFailed, 'note'));
      }
    }
    publicURL() {
      try {
        const u = new URL(this.config.publicURL);
        if (u.protocol !== 'https:' || u.username || u.password || u.port || !u.hostname.includes('.') || /localhost|127\.|\.local$|\.test$|\.invalid$|preview|ngrok|trycloudflare/i.test(u.hostname + u.search) || /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(u.hostname)) return '';
        return u.href;
      } catch { return ''; }
    }
    celebrationScene() {
      const scene = this.el('div', 'celebration');
      scene.setAttribute('aria-label', 'ラッコのキラ、ジュゴンのセレナ、スナドリネコのサニーがお祝い');
      const rays = this.el('div', 'celebration-rays'); rays.setAttribute('aria-hidden', 'true'); scene.append(rays);
      const stars = this.el('div', 'celebration-stars'); stars.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < 12; i++) {
        const star = this.el('span', '', i % 3 === 0 ? '✦' : '★');
        star.style.cssText = `left:${5 + i * 29 % 90}%;top:${8 + i * 19 % 78}%;--spark-delay:${i * 90}ms`; stars.append(star);
      }
      scene.append(stars);
      const cast = this.el('div', 'celebration-cast');
      for (const [index,id] of ['sunny','kira','serena'].entries()) {
        const animal = this.data.find(a => a.id === id); if (!animal) continue;
        const figure = this.el('figure', 'celebration-animal');
        figure.style.setProperty('--cast-delay', `${index * 180}ms`);
        if (id === 'kira') { const crown = this.el('span', 'celebration-crown', '♛'); crown.setAttribute('aria-hidden','true'); figure.append(crown); }
        figure.append(this.photo(animal), this.el('figcaption', '', animal.name)); cast.append(figure);
      }
      scene.append(cast,this.paragraph('みんなに会えたね！', 'celebration-ribbon'));
      if (this.config.effects === false) scene.classList.add('toba-quiz-celebration-static');
      return scene;
    }
    celebrate(target) {
      if (!target) return;
      this.playSound('complete');
      if (this.config.effects === false || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      target.querySelector('.toba-quiz-celebration')?.getAnimations?.({subtree:true}).forEach(animation => { animation.currentTime = 0; animation.play(); });
      target.querySelector('.toba-quiz-confetti')?.remove();
      const layer = this.el('div', 'confetti'); layer.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < 72; i++) {
        const piece = this.el('span');
        piece.style.cssText = `left:${(i * 37) % 100}%;--fall-delay:${i % 8 * 70}ms;--drift:${(i % 5 - 2) * 30}px;background:${['#2eabb9','#e9b74e','#f49c9e','#749bd0','#6fbba0'][i % 5]}`;
        layer.append(piece);
      }
      target.append(layer);
      clearTimeout(this.celebrationTimer);
      this.celebrationTimer = setTimeout(() => layer.remove(), 4400);
    }
    shareText() {
      return this.screen === 'collection' && this.saved.size === 10
        ? '鳥羽水族館チャームクイズで図鑑10種類コンプリート！\nあなたもチャームの仲間たちに会いにきてね。\n#鳥羽水族館 #めじるしチャーム #チャームクイズ'
        : `鳥羽水族館チャームクイズで5問中${this.score}問正解！\nあなたは何問わかる？\n#鳥羽水族館 #めじるしチャーム`;
    }
    socialButtons() {
      const box = this.el('section', 'social'); box.setAttribute('aria-label', 'SNSでシェア');
      box.append(this.el('h3', '', this.saved.size === 10 && this.screen === 'collection' ? 'コンプリートの記念をシェア' : 'クイズの結果をシェア'));
      const buttons = this.el('div', 'social-buttons');
      const url = this.publicURL();
      for (const [name,kind,endpoint] of [['Xでシェア','x','https://x.com/intent/tweet'],['LINEで送る','line','https://social-plugins.line.me/lineit/share']]) {
        if (url) {
          const link = this.el('a', 'social-link toba-quiz-social-' + kind, name);
          const target = new URL(endpoint); target.searchParams.set('url', url); target.searchParams.set('text', this.shareText());
          link.href = target.href; link.target = '_blank'; link.rel = 'noopener noreferrer';
          link.setAttribute('aria-label', name + '（新しい画面で開きます）'); buttons.append(link);
        } else {
          const button = this.button(name, '', true); button.disabled = true; buttons.append(button);
        }
      }
      const instagram = this.button('Instagram用画像を作る', 'instagram', true);
      instagram.classList.add('toba-quiz-social-instagram'); buttons.append(instagram); box.append(buttons);
      if (!url) box.append(this.paragraph('X・LINEのリンク共有は、クイズの公開後に利用できます。', 'small'));
      box.append(this.paragraph('Instagramは画像を保存して、アプリから投稿できます。', 'small'));
      return box;
    }
    clearShareImage() {
      clearTimeout(this.celebrationTimer);
      if (this.shareImageURL) URL.revokeObjectURL(this.shareImageURL);
      this.shareImageURL = null;
      this.shareImageGeneration = (this.shareImageGeneration || 0) + 1;
    }
    async instagram(button) {
      const output = this.main.querySelector('.toba-quiz-sharing'); if (!output) return;
      const generation = this.shareImageGeneration;
      const complete = this.screen === 'collection' && this.saved.size === 10;
      const animals = complete ? this.data : this.questions;
      button.disabled = true;
      output.replaceChildren(this.paragraph('記念画像を作っています…', 'small'));
      try {
        const canvas = document.createElement('canvas'); canvas.width = 1080; canvas.height = 1350;
        const ctx = canvas.getContext('2d'); if (!ctx) throw new Error('canvas');
        const bg = ctx.createLinearGradient(0,0,1080,1350); bg.addColorStop(0,'#dff5f1'); bg.addColorStop(1,'#eff5ff'); ctx.fillStyle = bg; ctx.fillRect(0,0,1080,1350);
        ctx.textAlign = 'center'; ctx.fillStyle = '#176178';
        const text = (value,y,size=40) => { ctx.font = `700 ${size}px "Yu Gothic", Meiryo, sans-serif`; ctx.fillText(value,540,y,980); };
        text('TOBA AQUARIUM',95,30); text('この子はだあれ？',170,54); text('鳥羽水族館チャームクイズ',235,44);
        ctx.fillStyle = '#087f92'; text(complete ? '★ 10種類コンプリート！ ★' : `5問中${this.score}問正解！`,345,57);
        ctx.fillStyle = '#176178'; text(complete ? 'CHARM MASTER' : '名前をおぼえて、もっと好きになる。',410,32);
        const images = await Promise.all(animals.map(a => new Promise(resolve => {
          const img = new Image(); img.crossOrigin = 'anonymous';
          const timer = setTimeout(() => { img.src = ''; resolve(null); }, 6000);
          img.onload = () => { clearTimeout(timer); resolve(img); };
          img.onerror = () => { clearTimeout(timer); resolve(null); }; img.src = a.image;
        })));
        if (generation !== this.shareImageGeneration || !this.isConnected) return;
        animals.forEach((animal,i) => {
          const col = i % 5, row = Math.floor(i / 5), x = 46 + col * 202, y = (complete ? 495 : 635) + row * 270;
          ctx.fillStyle = '#ffffff'; ctx.fillRect(x,y,180,246);
          const image = images[i];
          if (image) { const scale = Math.min(150/image.width,180/image.height); ctx.drawImage(image,x+90-image.width*scale/2,y+12,image.width*scale,image.height*scale); }
          else { ctx.fillStyle = '#d4eaf1'; ctx.fillRect(x+20,y+16,140,170); }
          ctx.fillStyle = '#176178'; ctx.font = '700 24px "Yu Gothic", Meiryo, sans-serif'; ctx.fillText(animal.name,x+90,y+224,170);
        });
        text('名前をおぼえたあの子たちを、毎日のそばに。',1160,28);
        text('#鳥羽水族館  #めじるしチャーム',1220,28);
        const url = this.publicURL(); if (url) text(url,1285,24);
        const blob = await new Promise(resolve => canvas.toBlob(resolve,'image/png')); if (!blob) throw new Error('image');
        if (generation !== this.shareImageGeneration || !this.isConnected) return;
        if (this.shareImageURL) URL.revokeObjectURL(this.shareImageURL);
        this.shareImageURL = URL.createObjectURL(blob);
        const image = this.el('img', 'share-card'); image.src = this.shareImageURL; image.alt = complete ? '図鑑10種類コンプリートの記念画像' : 'チャームクイズ結果の記念画像'; image.width = 1080; image.height = 1350;
        const download = this.el('a', 'btn', '画像を保存'); download.href = this.shareImageURL; download.download = complete ? 'toba-charm-complete.png' : 'toba-charm-result.png';
        output.replaceChildren(image,download,this.button('投稿文をコピー', 'copy-caption', true),this.paragraph('画像を保存し、Instagramの投稿・ストーリーズで選んでください。保存できない場合は画像を長押ししてください。', 'small'));
        if (images.some(image => !image)) output.append(this.paragraph('一部の写真を読み込めなかったため、お名前のみで作成しました。写真を入れる場合はもう一度お試しください。', 'small'));
      } catch {
        if (generation === this.shareImageGeneration) output.replaceChildren(this.paragraph('画像を作成できませんでした。画面のスクリーンショットをご利用ください。', 'small'),this.button('投稿文をコピー', 'copy-caption', true));
      } finally { if (button.isConnected) button.disabled = false; }
    }
    async copyCaption(button) {
      const value = this.shareText() + (this.publicURL() ? '\n' + this.publicURL() : '');
      try { await navigator.clipboard.writeText(value); if (button.isConnected) button.textContent = '投稿文をコピーしました'; }
      catch {
        const output = this.main.querySelector('.toba-quiz-sharing');
        if (!output || !button.isConnected) return;
        output.querySelector('.toba-quiz-share-text')?.remove();
        const area = this.el('textarea','share-text'); area.readOnly = true; area.value = value; area.setAttribute('aria-label','コピー用の投稿文'); output.append(area); area.focus(); area.select();
      }
    }
    async share(button) {
      const url = this.publicURL(); if (!url) return;
      const output = this.main.querySelector('.toba-quiz-sharing');
      const text = this.shareText();
      button.disabled = true;
      try {
        if (navigator.share) {
          try { await navigator.share({title:this.text.gameName, text, url}); return; }
          catch (error) { if (error.name === 'AbortError') return; }
        }
        try {
          await navigator.clipboard.writeText(`${text}\n${url}`);
          output.replaceChildren(this.paragraph(this.text.copied, 'small'));
        } catch {
          const label = this.el('label', '', this.text.manualCopy);
          const area = this.el('textarea', 'share-text'); area.readOnly = true; area.value = `${text}\n${url}`;
          label.append(area); output.replaceChildren(label, this.button(this.text.selectText, 'select-share', true));
          area.focus(); area.select();
        }
      } finally { button.disabled = false; }
    }
  }
  customElements.define(TAG, TobaQuiz);
})();

